import { useMemo } from 'react';
import {
  PageBlueprint,
  coreExtensionData,
  createExtensionInput,
} from '@backstage/frontend-plugin-api';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import {
  EntityListDocsTable,
  EntityTechdocsContent,
  TechDocsPicker,
} from '@backstage/plugin-techdocs';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { useEffect } from 'react';
import {
  CatalogFilterLayout,
  EntityListProvider,
  EntityOwnerPicker,
  EntityTagPicker,
  UserListPicker,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { isExternalUserSync, useIsExternalUser } from '../externalUser';
import { Container } from '@backstage/ui';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import {
  applyColumnLayout,
  ColumnLayoutPersistence,
  persistedTableOptions,
} from '../tableColumnLayout';
import { Pill, TypePill } from '../../components/tablePills';

// Renders nothing; for external users it pins a catalog filter so the docs
// index only lists entities whose docs are shared externally
// (savr.com/external-docs) - everything else would 403 via the backend's
// techdocsExternalGuard anyway.
function ExternalDocsFilter() {
  const isExternal = useIsExternalUser();
  const { updateFilters } = useEntityList<any>();
  useEffect(() => {
    updateFilters({
      externalDocs:
        isExternal === true
          ? {
              getCatalogFilters: () => ({
                'metadata.annotations.savr.com/external-docs': 'true',
              }),
            }
          : undefined,
    } as any);
  }, [isExternal, updateFilters]);
  return null;
}

// The stock docs index page passes no options to its table and its page
// content component isn't exported, so this replicates
// TechDocsIndexPageContent (same filters and default columns) with the shared
// draggable/resizable column layout applied (see ../tableColumnLayout).
function SavrDocsPage() {
  const columns = useMemo(
    () =>
      applyColumnLayout([
        EntityListDocsTable.columns.createTitleColumn({ hidden: true }),
        EntityListDocsTable.columns.createNameColumn(),
        EntityListDocsTable.columns.createOwnerColumn(),
        {
          ...EntityListDocsTable.columns.createKindColumn(),
          render: ({ entity }) => <Pill label={entity.kind} tone="neutral" />,
        },
        {
          ...EntityListDocsTable.columns.createTypeColumn(),
          render: ({ entity }) => <TypePill type={entity.spec?.type} />,
        },
      ]),
    [],
  );
  return (
    <ColumnLayoutPersistence>
      <Container mt="6">
        <EntityListProvider>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <ExternalDocsFilter />
              <TechDocsPicker />
              <UserListPicker initialFilter="owned" />
              <EntityOwnerPicker />
              <EntityTagPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <EntityListDocsTable
                columns={columns}
                options={persistedTableOptions}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Container>
    </ColumnLayoutPersistence>
  );
}

const techDocsIndexPage = PageBlueprint.make({
  params: {
    path: '/docs',
    routeRef: techdocsPlugin.routes.root,
    title: 'Docs',
    icon: <LibraryBooksIcon fontSize="inherit" />,
    loader: async () => <SavrDocsPage />,
  },
});

// Replaces the plugin's stock entity tab (same unnamed extension id) to hide
// it for external users unless the entity opts in via savr.com/external-docs.
// The real enforcement is the backend's techdocsExternalGuard; this only
// avoids showing a tab that would 403. The emptyState input mirrors the stock
// extension so the plugin's own empty-state attachment doesn't dangle. Note:
// TechDocs addons (new-frontend-system AddonBlueprint ones) are not re-wired
// into this replacement - they flow through an internal addons API that isn't
// exported - so addons only apply on the standalone /docs reader page.
const savrTechDocsEntityContent = EntityContentBlueprint.makeWithOverrides({
  inputs: {
    emptyState: createExtensionInput(
      [coreExtensionData.reactElement.optional()],
      {
        singleton: true,
        optional: true,
      },
    ),
  },
  factory(originalFactory, context) {
    return originalFactory(
      {
        path: 'docs',
        title: 'TechDocs',
        group: 'documentation',
        routeRef: techdocsPlugin.routes.entityContent,
        filter: entity =>
          !isExternalUserSync() ||
          entity.metadata.annotations?.['savr.com/external-docs'] === 'true',
        loader: async () => <EntityTechdocsContent />,
      },
      context,
    );
  },
});

export const techdocsPluginWithOverrides = techdocsPlugin.withOverrides({
  extensions: [techDocsIndexPage, savrTechDocsEntityContent],
});
